import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconFolder, IconPlus, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type Source, type SourceFile, useAdminMutation, useAdminQuery } from "../../api/admin";
import { api } from "../../api/client";
import { useCompanyOptions, useNames } from "../../api/hooks";
import { formatDateTime } from "../../lib/format";
import { InboxPage } from "./inbox-page";

interface SourcesResponse {
  roots: string[];
  inbox: { path: string; mode: string };
  sources: Source[];
}

interface Form {
  name: string;
  path: string;
  mode: "reference" | "copy";
  layout: "any" | "company-type";
  companyId: string | null;
  docTypeId: string | null;
  scan: "interval" | "live";
  scanMinutes: number;
  windowsPrefix: string;
  enabled: boolean;
}

const emptyForm: Form = {
  name: "",
  path: "",
  mode: "reference",
  layout: "any",
  companyId: null,
  docTypeId: null,
  scan: "interval",
  scanMinutes: 10,
  windowsPrefix: "",
  enabled: true,
};

const STATE_COLOR: Record<string, string> = {
  ok: "green",
  scanning: "blue",
  idle: "gray",
  unavailable: "red",
  guarded: "orange",
  error: "red",
};

/**
 * Folder sources: the classic inbox (from .env) and the folders an administrator added. The
 * worker walks them; this page edits them, asks for a walk and shows what the walks found.
 */
export function SourcesPage() {
  const { t } = useTranslation();
  const { lookups, docType } = useNames();
  const data = useAdminQuery<SourcesResponse>("sources", "/api/admin/sources", 10_000);
  const [editing, setEditing] = useState<Source | "new" | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [removing, setRemoving] = useState<Source | null>(null);
  const [files, setFiles] = useState<{ source: Source; kind: "missing" | "excluded" } | null>(null);
  const companyOptions = useCompanyOptions(form.companyId);

  const save = useAdminMutation(
    (v: { id?: number; body: Record<string, unknown> }) =>
      api<Source>(v.id ? `/api/admin/sources/${v.id}` : "/api/admin/sources", {
        method: v.id ? "PATCH" : "POST",
        body: v.body,
      }),
    ["sources", "inbox"],
  );
  const action = useAdminMutation(
    (v: { id: number; what: "scan" | "accept-missing" | "delete" }) =>
      v.what === "delete"
        ? api<{ id: number; retired?: number }>(`/api/admin/sources/${v.id}`, { method: "DELETE" })
        : api<{ id: number; retired?: number }>(`/api/admin/sources/${v.id}/${v.what}`, {
            method: "POST",
          }),
    ["sources", "inbox"],
  );
  const toggle = useAdminMutation(
    (v: { id: number; enabled: boolean }) =>
      api<Source>(`/api/admin/sources/${v.id}`, { method: "PATCH", body: { enabled: v.enabled } }),
    ["sources"],
  );

  const fail = (e: Error) => notifications.show({ message: e.message, color: "red" });
  const roots = data.data?.roots ?? [];
  const sources = data.data?.sources ?? [];

  const openForm = (s: Source | "new") => {
    setEditing(s);
    setForm(
      s === "new"
        ? emptyForm
        : {
            name: s.name,
            path: s.path,
            mode: s.mode,
            layout: s.layout,
            companyId: s.companyId ? String(s.companyId) : null,
            docTypeId: s.docTypeId ? String(s.docTypeId) : null,
            scan: s.scan,
            scanMinutes: s.scanMinutes,
            windowsPrefix: s.windowsPrefix ?? "",
            enabled: s.enabled,
          },
    );
  };

  const submit = () => {
    const body: Record<string, unknown> = {
      name: form.name.trim(),
      mode: form.mode,
      layout: form.layout,
      companyId: form.companyId ? Number(form.companyId) : null,
      docTypeId: form.docTypeId ? Number(form.docTypeId) : null,
      scan: form.scan,
      scanMinutes: form.scanMinutes,
      windowsPrefix: form.windowsPrefix.trim() || null,
      enabled: form.enabled,
    };
    if (editing === "new") body.path = form.path.trim();
    save
      .mutateAsync(editing === "new" || editing === null ? { body } : { id: editing.id, body })
      .then(() => {
        notifications.show({ message: t("sources.saved"), color: "green" });
        setEditing(null);
      })
      .catch(fail);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Text size="sm" c="dimmed" maw={720}>
          {t("sources.hint")}
        </Text>
        <Tooltip label={t("sources.noRoots")} disabled={roots.length > 0} multiline w={300}>
          <Button
            leftSection={<IconPlus size={16} />}
            disabled={roots.length === 0}
            onClick={() => openForm("new")}
          >
            {t("sources.add")}
          </Button>
        </Tooltip>
      </Group>

      {data.data && (
        <Paper withBorder p="sm">
          <Group gap="xs">
            <IconFolder size={16} />
            <Text fw={600}>{t("sources.inbox")}</Text>
            <Badge size="xs" variant="light" color="gray">
              .env
            </Badge>
          </Group>
          <Text size="sm" c="dimmed" style={{ wordBreak: "break-all" }}>
            {t("sources.inboxHint", { path: data.data.inbox.path, mode: data.data.inbox.mode })}
          </Text>
        </Paper>
      )}

      {data.data && sources.length === 0 && (
        <Text c="dimmed" size="sm">
          {roots.length ? t("sources.empty") : t("sources.noRoots")}
        </Text>
      )}

      {sources.map((s) => (
        <Paper key={s.id} withBorder p="sm">
          <Stack gap={6}>
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Group gap="xs">
                  <Text fw={600}>{s.name}</Text>
                  <Badge size="xs" variant="light" color={STATE_COLOR[s.state] ?? "gray"}>
                    {t(`sources.states.${s.state}`, s.state)}
                  </Badge>
                  <Badge
                    size="xs"
                    variant="outline"
                    color={s.mode === "reference" ? "blue" : "gray"}
                  >
                    {t(`sources.modes.${s.mode}`)}
                  </Badge>
                  <Badge size="xs" variant="outline" color="gray">
                    {s.scan === "live"
                      ? t("sources.scans.live")
                      : t("sources.everyMinutes", { count: s.scanMinutes })}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed" style={{ wordBreak: "break-all" }}>
                  {s.windowsPrefix ? `${s.windowsPrefix} (${s.path})` : s.path}
                </Text>
                <Text size="xs">
                  {t("sources.counts", {
                    documents: s.documents,
                    files: s.files,
                    missing: s.missing,
                    problems: s.problems,
                    indexing: s.indexing,
                  })}
                </Text>
                <Text size="xs" c="dimmed">
                  {s.lastScanAt
                    ? t("sources.lastScan", { at: formatDateTime(s.lastScanAt) })
                    : t("sources.never")}
                </Text>
              </Stack>
              <Switch
                checked={s.enabled}
                aria-label={t("sources.enabled")}
                styles={{ track: { cursor: "pointer" } }}
                onChange={(e) =>
                  toggle.mutateAsync({ id: s.id, enabled: e.currentTarget.checked }).catch(fail)
                }
              />
            </Group>
            {s.state === "guarded" && (
              <Alert color="orange" variant="light" title={t("sources.guardedTitle")}>
                <Stack gap="xs">
                  <Text size="sm">{s.lastError}</Text>
                  <Text size="xs" c="dimmed">
                    {t("sources.acceptMissingHint")}
                  </Text>
                  <Group>
                    <Button
                      size="compact-sm"
                      color="orange"
                      loading={action.isPending && action.variables?.id === s.id}
                      onClick={() =>
                        action
                          .mutateAsync({ id: s.id, what: "accept-missing" })
                          .then(() =>
                            notifications.show({
                              message: t("sources.scanRequested"),
                              color: "green",
                            }),
                          )
                          .catch(fail)
                      }
                    >
                      {t("sources.acceptMissing")}
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            )}
            {s.lastError && s.state !== "guarded" && (
              <Text size="xs" c={s.state === "ok" ? "dimmed" : "red"}>
                {s.lastError}
              </Text>
            )}
            <Group gap="xs">
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                disabled={!s.enabled}
                onClick={() =>
                  action
                    .mutateAsync({ id: s.id, what: "scan" })
                    .then(() =>
                      notifications.show({ message: t("sources.scanRequested"), color: "green" }),
                    )
                    .catch(fail)
                }
              >
                {t("sources.scanNow")}
              </Button>
              <Button size="compact-xs" variant="default" onClick={() => openForm(s)}>
                {t("common.edit")}
              </Button>
              {s.missing + s.missingPending > 0 && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => setFiles({ source: s, kind: "missing" })}
                >
                  {t("sources.missingFiles", { count: s.missing + s.missingPending })}
                </Button>
              )}
              {s.excluded > 0 && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => setFiles({ source: s, kind: "excluded" })}
                >
                  {t("sources.excludedFiles", { count: s.excluded })}
                </Button>
              )}
              <Button size="compact-xs" variant="subtle" color="red" onClick={() => setRemoving(s)}>
                {t("sources.delete")}
              </Button>
            </Group>
          </Stack>
        </Paper>
      ))}

      <Text fw={600} mt="sm">
        {t("sources.problems")}
      </Text>
      <InboxPage />

      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? t("sources.add") : t("common.edit")}
        size="lg"
      >
        <Stack>
          <TextInput
            label={t("sources.name")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            required
          />
          <TextInput
            label={t("sources.path")}
            description={
              editing === "new"
                ? t("sources.rootsHint", { roots: roots.join(", ") })
                : t("sources.pathFixed")
            }
            value={form.path}
            disabled={editing !== "new"}
            placeholder={roots[0] ? `${roots[0]}/…` : ""}
            onChange={(e) => setForm({ ...form, path: e.currentTarget.value })}
            required
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t("sources.mode")}
            </Text>
            <SegmentedControl
              value={form.mode}
              onChange={(v) => setForm({ ...form, mode: v as Form["mode"] })}
              data={[
                { value: "reference", label: t("sources.modes.reference") },
                { value: "copy", label: t("sources.modes.copy") },
              ]}
            />
            <Text size="xs" c="dimmed">
              {t(`sources.modeHints.${form.mode}`)}
            </Text>
          </Stack>
          <Select
            label={t("sources.layout")}
            description={t("sources.layoutHint")}
            data={[
              { value: "any", label: t("sources.layouts.any") },
              { value: "company-type", label: t("sources.layouts.company-type") },
            ]}
            value={form.layout}
            onChange={(v) => setForm({ ...form, layout: (v ?? "any") as Form["layout"] })}
            allowDeselect={false}
          />
          <Group grow>
            <Select
              label={t("sources.defaultCompany")}
              data={companyOptions}
              value={form.companyId}
              onChange={(v) => setForm({ ...form, companyId: v })}
              clearable
              placeholder={t("sources.detected")}
            />
            <Select
              label={t("sources.defaultType")}
              data={(lookups.data?.docTypes ?? []).map((d) => ({
                value: String(d.id),
                label: docType(d.id),
              }))}
              value={form.docTypeId}
              onChange={(v) => setForm({ ...form, docTypeId: v })}
              clearable
              placeholder={t("sources.detected")}
            />
          </Group>
          <Group grow align="flex-end">
            <Select
              label={t("sources.scan")}
              description={t("sources.scanHint")}
              data={[
                { value: "interval", label: t("sources.scans.interval") },
                { value: "live", label: t("sources.scans.live") },
              ]}
              value={form.scan}
              onChange={(v) => setForm({ ...form, scan: (v ?? "interval") as Form["scan"] })}
              allowDeselect={false}
            />
            <NumberInput
              label={t("sources.minutes")}
              min={1}
              max={1440}
              value={form.scanMinutes}
              onChange={(v) => setForm({ ...form, scanMinutes: Number(v) || 10 })}
            />
          </Group>
          <TextInput
            label={t("sources.windowsPrefix")}
            description={t("sources.windowsPrefixHint")}
            placeholder="\\fileserver\audit"
            value={form.windowsPrefix}
            onChange={(e) => setForm({ ...form, windowsPrefix: e.currentTarget.value })}
          />
          <Switch
            label={t("sources.enabled")}
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.currentTarget.checked })}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={save.isPending}
              disabled={!form.name.trim() || (editing === "new" && !form.path.trim())}
              onClick={submit}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        title={t("sources.delete")}
      >
        <Stack>
          <Text size="sm">{t("sources.deleteConfirm", { name: removing?.name ?? "" })}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRemoving(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={action.isPending}
              onClick={() => {
                if (!removing) return;
                action
                  .mutateAsync({ id: removing.id, what: "delete" })
                  .then((r) => {
                    notifications.show({
                      message: t("sources.deleted", { count: r.retired ?? 0 }),
                      color: "green",
                    });
                    setRemoving(null);
                  })
                  .catch(fail);
              }}
            >
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <SourceFilesModal value={files} onClose={() => setFiles(null)} />
    </Stack>
  );
}

function SourceFilesModal({
  value,
  onClose,
}: {
  value: { source: Source; kind: "missing" | "excluded" } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const list = useAdminQuery<{ items: SourceFile[] }>(
    `sources-files-${value?.source.id ?? 0}-${value?.kind ?? ""}`,
    value ? `/api/admin/sources/${value.source.id}/files?kind=${value.kind}` : "/api/admin/sources",
  );
  const items = value ? (list.data?.items ?? []) : [];
  return (
    <Modal
      opened={value !== null}
      onClose={onClose}
      size="lg"
      title={
        value
          ? `${value.source.name}: ${t(value.kind === "missing" ? "sources.missingTitle" : "sources.excludedTitle")}`
          : ""
      }
    >
      {items.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t("sources.noFiles")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={480}>
          <Table striped>
            <Table.Tbody>
              {items.map((f) => (
                <Table.Tr key={f.id}>
                  <Table.Td style={{ wordBreak: "break-all" }}>{f.relPath}</Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {f.present
                        ? t("sources.missingOnce")
                        : f.missingSince
                          ? formatDateTime(f.missingSince)
                          : ""}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Modal>
  );
}
